import { AuthTokenStorageService } from '@/lib/auth';
import { IFilter, IItemResponse, IOptions, UserDTO } from '@/shared';
import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import useDebounce from '@/hooks/useDebounce';
import {
  checkSkillCompatibility,
  getCompanyJobList,
  getJobAutocomplete,
  getJobDetail,
  getRelatedJobs,
  getSavedJobIds,
  getSavedJobs,
  getSkillCompatibility,
  getSuggestedJobList,
  getTopCountWorks,
  getActiveProfessions,
  getBannerJobList,
  increaseShareCount,
  saveJob,
  searchJobs,
  setBannerJobClickCount,
  sendCV,
  getJobInsight,
  activateZangiaPlus,
  fetchHomeSearch,
  getZangiaPlusReport,
  updateZangiaPlusAdditional,
} from '@/features/jobs/api/jobs.api';
import { jobQueryKeys } from '@/features/jobs/api/jobs.keys';
import {
  useGuestJobViewGate,
  useGuestSearchGate,
} from '@/features/jobs/hooks/useGuestGate';
import { applicantsQueryKeys } from '@/features/applicants/api/applicants.keys';
import { userQueryKeys } from '@/features/user/api/user.keys';
import { getMutationErrorMessage } from '@/lib/api';
import { queryClient } from '@/lib/queryClient';
import {
  AutocompleteResponseDto,
  JobListFilterDto,
  PublicJobDetailDTO,
  PublicJobListDTO,
} from '@/shared/job/job.public.dto';
import {
  DashboardStatsDTO,
  ZangiaPlusAdditionalKey,
  ZangiaPlusReportDTO,
} from '@/shared/user/dashboard';
import { InsightDataDto } from '@/shared/job/insight.dto';
import { hasActiveZangiaPlus } from '@/features/jobs/lib/hasActiveZangiaPlus';
import { aiQueryKeys } from '@/features/cv/api/ai.keys';

export { jobQueryKeys } from '@/features/jobs/api/jobs.keys';

const EMPTY_SAVED_JOBS_FILTER: IFilter = {};
const SAVED_JOBS_STALE_TIME = 5 * 60 * 1000;

export function useGetSavedJobs(params: IFilter = EMPTY_SAVED_JOBS_FILTER) {
  const token = AuthTokenStorageService.getAccessToken();
  return useQuery({
    queryKey: jobQueryKeys.getSavedJobs.list(params),
    queryFn: () => getSavedJobs(params),
    enabled: Boolean(token),
    staleTime: SAVED_JOBS_STALE_TIME,
    retryOnMount: false,
  });
}

export function useSavedJobIds() {
  const token = AuthTokenStorageService.getAccessToken();
  return useQuery<number[]>({
    queryKey: jobQueryKeys.savedJobIds,
    queryFn: getSavedJobIds,
    enabled: Boolean(token),
    staleTime: SAVED_JOBS_STALE_TIME,
    retryOnMount: false,
  });
}

export function useSaveJob() {
  return useMutation({
    mutationFn: (jobId: number) => saveJob(jobId),
    onSuccess: (_data, jobId) => {
      const id = Number(jobId);
      const previous =
        queryClient.getQueryData<number[]>(jobQueryKeys.savedJobIds) ?? [];
      const wasSaved = previous.some((savedId) => Number(savedId) === id);

      queryClient.setQueryData<number[]>(
        jobQueryKeys.savedJobIds,
        wasSaved
          ? previous.filter((savedId) => Number(savedId) !== id)
          : [...previous, id]
      );

      queryClient.removeQueries({
        queryKey: jobQueryKeys.getSavedJobs.all,
      });

      queryClient.setQueryData<DashboardStatsDTO>(
        userQueryKeys.dashboardStats,
        (old) => {
          if (!old) return old;
          const current = old.savedCount ?? 0;
          return {
            ...old,
            savedCount: Math.max(0, current + (wasSaved ? -1 : 1)),
          };
        }
      );

      toast.success(
        wasSaved ? 'Хадгалалтаас хаслаа' : 'Амжилттай хадгалагдлаа'
      );
    },
    onError: (error: unknown) => {
      toast.error(getMutationErrorMessage(error));
    },
  });
}

export function useSendCv() {
  return useMutation({
    mutationFn: (jobId: number) => sendCV(jobId),
    onSuccess: (_, jobId) => {
      const id = Number(jobId);
      queryClient.setQueryData<DashboardStatsDTO>(
        userQueryKeys.dashboardStats,
        (old) => {
          if (!old) return old;
          return {
            ...old,
            sentCount: (old.sentCount ?? 0) + 1,
          };
        }
      );

      queryClient.setQueriesData<PublicJobDetailDTO>(
        { queryKey: ['job', 'detail'] },
        (old) => {
          if (!old || Number(old.id) !== id) return old;
          if (old.isApplicant) return old;
          return { ...old, isApplicant: true };
        }
      );

      queryClient.removeQueries({
        queryKey: applicantsQueryKeys.all,
      });

      const user = queryClient.getQueryData<UserDTO>(userQueryKeys.me);
      if (hasActiveZangiaPlus(user)) {
        void queryClient.invalidateQueries({
          queryKey: jobQueryKeys.jobInsight(id),
        });
      }
    },
    onError: (error: unknown) => {
      toast.error(
        getMutationErrorMessage(
          error,
          'Алдаа гарлаа. Та дахин оролдож үзнэ үү!'
        )
      );
    },
  });
}

export function useGetJobDetail(
  code: string | number,
  options?: {
    enabled?: boolean;
    /** false бол нэвтрээгүй хэрэглэгчийн зар үзэх хязгаарт тоолохгүй (жишээ нь insight-ын time авах) */
    countView?: boolean;
  }
) {
  const enabled = options?.enabled ?? !!code;
  const countView = options?.countView ?? true;
  const gate = useGuestJobViewGate(code, enabled && countView);
  const query = useQuery({
    queryKey: jobQueryKeys.detail.get(code),
    queryFn: () => getJobDetail(code),
    enabled: enabled && gate.isAllowed,
  });

  return {
    ...query,
    isLoading: query.isLoading || (enabled && gate.isPending),
    guestLimitMessage: gate.blockedMessage,
  };
}

export function useGetCompanyJobList(companyId: number) {
  return useQuery({
    queryKey: jobQueryKeys.company(companyId),
    queryFn: () => getCompanyJobList(companyId),
    enabled: !!companyId,
  });
}

export function useGetTopCountWorks() {
  return useQuery({
    queryKey: jobQueryKeys.topCountWorks,
    queryFn: () => getTopCountWorks(),
  });
}

export function useActiveProfessions(branchId: number) {
  return useQuery({
    queryKey: jobQueryKeys.activeProfessions(branchId),
    queryFn: () => getActiveProfessions(branchId),
    enabled: !!branchId,
    select: (res): IOptions[] => res?.data ?? [],
  });
}

export function useSearchJobs(params: JobListFilterDto = {}, enabled = true) {
  const gate = useGuestSearchGate(params, enabled);
  const query = useQuery({
    queryKey: jobQueryKeys.searchJobs.get(params),
    queryFn: () => searchJobs(params),
    enabled: enabled && gate.isAllowed,
  });

  return {
    ...query,
    isLoading: query.isLoading || (enabled && gate.isPending),
    guestLimitMessage: gate.blockedMessage,
  };
}

export function useBannerJobList() {
  return useQuery({
    queryKey: jobQueryKeys.bannerJobList,
    queryFn: getBannerJobList,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 10,
    refetchOnWindowFocus: false,
  });
}

export function useBannerJobClickCount() {
  return useMutation({
    mutationFn: (id: number) => setBannerJobClickCount(id),
  });
}

export function useGetRelatedJobs(jobId: number | string) {
  return useQuery({
    queryKey: jobQueryKeys.relatedJobs.get(jobId),
    queryFn: () => getRelatedJobs(jobId),
    enabled: !!jobId,
  });
}

export function useShareJob() {
  return useMutation({
    mutationFn: (id: number) => increaseShareCount(id),
  });
}

export function useSuggestedJobList() {
  const token = AuthTokenStorageService.getAccessToken();

  return useQuery<(PublicJobListDTO & { matchLevel?: number })[]>({
    queryKey: jobQueryKeys.suggestedJobList,
    queryFn: getSuggestedJobList,
    enabled: !!token,
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
  });
}

export function useJobAutocomplete(query: string) {
  const debouncedQuery = useDebounce(query, 300);
  return useQuery<AutocompleteResponseDto[]>({
    queryKey: jobQueryKeys.autocomplete(debouncedQuery),
    queryFn: () => getJobAutocomplete(debouncedQuery),
    enabled: debouncedQuery.trim().length > 0,
  });
}
export function useHomeSearch(query: string) {
  const debouncedQuery = useDebounce(query, 300);
  return useQuery<string[]>({
    queryKey: jobQueryKeys.homeSearch(debouncedQuery),
    queryFn: () => fetchHomeSearch(debouncedQuery),
    enabled: debouncedQuery.trim().length > 0,
  });
}

export function useGetSkillCompatibility(jobId: number) {
  const token = AuthTokenStorageService.getAccessToken();

  return useQuery({
    queryKey: jobQueryKeys.skillCompatibility(jobId),
    queryFn: () => getSkillCompatibility(jobId),
    enabled: !!token && !!jobId,
    retry: false,
  });
}

export function useCheckSkillCompatibility() {
  return useMutation({
    mutationFn: ({ jobId, langId }: { jobId: number; langId?: number }) =>
      checkSkillCompatibility(jobId, langId),
    onSuccess: (data, variables) => {
      queryClient.setQueryData(
        jobQueryKeys.skillCompatibility(variables.jobId),
        data
      );
    },
  });
}

export function useGetJobInsight(jobId: number) {
  return useQuery<IItemResponse<InsightDataDto>>({
    queryKey: jobQueryKeys.jobInsight(jobId),
    queryFn: () => getJobInsight(jobId),
    enabled: !!jobId,
  });
}

export function useActivateZangiaPlus() {
  return useMutation({
    mutationFn: activateZangiaPlus,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userQueryKeys.me });
      queryClient.invalidateQueries({
        queryKey: userQueryKeys.transactionSummary,
      });
      queryClient.invalidateQueries({
        queryKey: ['auth', 'user-transactions'],
      });
      queryClient.invalidateQueries({
        queryKey: jobQueryKeys.zangiaPlusReport,
      });
      queryClient.invalidateQueries({ queryKey: aiQueryKeys.cvAiUsage });
    },
    onError: (error: unknown) => {
      toast.error(getMutationErrorMessage(error, 'Алдаа гарлаа'));
    },
  });
}

export function useZangiaPlusReport(enabled = true) {
  const token = AuthTokenStorageService.getAccessToken();

  return useQuery<ZangiaPlusReportDTO>({
    queryKey: jobQueryKeys.zangiaPlusReport,
    queryFn: getZangiaPlusReport,
    enabled: Boolean(token) && enabled,
  });
}

export function useUpdateZangiaPlusAdditional() {
  return useMutation({
    mutationFn: (keys: ZangiaPlusAdditionalKey[]) =>
      updateZangiaPlusAdditional(keys),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: jobQueryKeys.zangiaPlusReport,
      });
    },
    onError: (error: unknown) => {
      toast.error(getMutationErrorMessage(error, 'Алдаа гарлаа'));
    },
  });
}
